import * as React from 'react';
import { Input } from '@noissue/os-ui/components/input';

export interface NumberInputProps
  extends Omit<React.ComponentProps<typeof Input>, 'value' | 'onChange' | 'type'> {
  /** The committed value. `null` renders an empty field. */
  value: number | null;
  /**
   * Called with the new value when editing FINISHES (blur, or Enter), and only when it
   * actually changed. Never called mid-typing, so a keystroke cannot trigger a refetch,
   * a re-price, or a mutation per character.
   */
  onValueChange: (value: number) => void;
  /**
   * Called on EVERY keystroke with the number the text represents so far — `null` while it
   * is empty or not yet a usable one — and with `null` again when editing finishes.
   *
   * For deriving something the rep READS while typing, and nothing else: the margin beside
   * the unit price has to move with the digits, or it reports the margin of the previous
   * price while the rep looks straight at a new one. Don't fetch, price or mutate from it
   * — that is what `onValueChange` is for, and why this component exists.
   *
   * Reset to `null` on commit rather than left holding the typed figure, so the caller
   * falls back to the value that actually LANDED — a revert, or an answer of the caller's
   * own, is a different number from the last thing typed.
   */
  onDraftChange?: (value: number | null) => void;
  /**
   * Called on commit when the text was empty, not a number, or out of range — INSTEAD of
   * `onValueChange`, which has no value to report.
   *
   * Optional, and leaving it out keeps the default: the field reverts to `value`. That is
   * what a quantity wants, since clearing one and tabbing away is not a request to order
   * nothing. A field with a derivable default wants the opposite — the unit price treats
   * an emptied field as "use the catalogue price" and clears the override — and only the
   * caller knows which of the two it is.
   */
  onCleared?: () => void;
  /** Rejected below this on commit; the field reverts (or clears) instead. */
  min?: number;
  /**
   * Decimal places to keep on commit. 0 (the default) floors to an integer, which is
   * what a quantity or a shipment count wants; a unit price wants 4, the precision the
   * engine reads prices at (`AC77`).
   */
  decimals?: number;
}

/**
 * A numeric field that lets you finish typing.
 *
 * The naive version — `onChange={(e) => setValue(Number(e.target.value))}` — is broken
 * for keyboard use, and subtly enough that it ships: `Number('')` is `0`, so backspacing
 * the last digit of "1000" writes 0 into state, the field re-renders as "0", and that
 * zero cannot be deleted. A rep replacing 1000 with 1500 ends up typing "01500". It bit
 * every field written that way here.
 *
 * So the text being edited lives in this component, uncoerced, and is parsed only on
 * exit. An empty field STAYS empty while focused. On exit a value that is empty, not a
 * number, or below `min` reverts to the last committed one rather than becoming zero —
 * clearing a field and tabbing away is not a request to order nothing. A caller with a
 * default worth falling back to can say so instead, with `onCleared`.
 *
 * The caller may answer a commit with a DIFFERENT number (see `commits`); the field
 * follows what the caller settles on, not what was typed.
 *
 * Commits to `decimals` places — 0 by default, which floors to an integer (quantities,
 * shipment counts). The two percentage inputs on the Freight & Duties step keep their own
 * string state and predate this.
 */
function NumberInput({
  value,
  onValueChange,
  onDraftChange,
  onCleared,
  min,
  decimals = 0,
  onBlur,
  onKeyDown,
  ...props
}: NumberInputProps) {
  const [text, setText] = React.useState(value === null ? '' : String(value));
  /**
   * Bumped by every commit so the text re-syncs from `value` even when `value` did not
   * change. Needed because a caller may ANSWER with a different number than the one
   * committed — the configurator snaps a quantity up to a whole carton — and doing that
   * twice running settles on the same value both times. Without this, the second commit
   * would leave the rejected figure ("12500") on screen against a value of 13,000.
   */
  const [commits, setCommits] = React.useState(0);

  // Re-sync when the committed value changes underneath us — a supplier default arriving,
  // a snapped answer, or the server normalising the number.
  React.useEffect(() => setText(value === null ? '' : String(value)), [value, commits]);

  /**
   * The number some text stands for, or null if it is not one a commit would accept.
   * Shared with `onDraftChange` so a draft the caller reads from cannot be a value this
   * field would go on to reject — typing "-" or a figure under `min` reads as no value,
   * not as a negative price with a margin over 100%.
   */
  const parse = (raw: string): number | null => {
    const factor = 10 ** decimals;
    const parsed = decimals === 0 ? Math.floor(Number(raw)) : Math.round(Number(raw) * factor) / factor;
    const valid = raw.trim() !== '' && Number.isFinite(parsed) && parsed >= (min ?? -Infinity);
    return valid ? parsed : null;
  };

  const commit = () => {
    const parsed = parse(text);
    if (parsed === null) onCleared?.();
    else if (parsed !== value) onValueChange(parsed);
    // Both in one handler, so the commit and the draft's release land in a single render.
    onDraftChange?.(null);
    // Whatever happened — reverted, unchanged, or answered with something else — the
    // field now shows the value the caller holds.
    setCommits((c) => c + 1);
  };

  return (
    <Input
      type="number"
      inputMode="numeric"
      min={min}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        onDraftChange?.(parse(e.target.value));
      }}
      onBlur={(e) => {
        commit();
        onBlur?.(e);
      }}
      onKeyDown={(e) => {
        // Enter commits by leaving the field, so one path handles both.
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        onKeyDown?.(e);
      }}
      {...props}
    />
  );
}

export { NumberInput };
